import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, ShoppingBag, Plus, Minus } from "lucide-react";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Best-Sellers / Repurchase Formulas — visual direction is frozen. This
// pass only: (1) tighter card info rhythm, (2) reliable on-card single-
// select variant picker (2 visible + "+N" popover styled like Quick
// Start), (3) Add to Cart on every card, (4) Quick Shop drawer kept but
// disconnected from cards, (5) Bundle & Save bar expands inline into an
// elevated Build-Your-Routine builder that reuses the drawer. All product
// data, prices, media and promo wording are official (riversol.com).
const PRODUCTS = [
  {
    id: "aatrio",
    title: "Anti-Aging Trio Bundle",
    href: "https://www.riversol.com/products/anti-aging-trio-bundle",
    description: "Daily age support trio with vitamin C & E and hinokitiol.",
    primary:
      "https://www.riversol.com/cdn/shop/products/AA-Normal.png?v=1776103480&width=1500",
    secondary:
      "https://www.riversol.com/cdn/shop/files/HydratingCreamCleanser_041ce683-1851-46de-be02-e0e850264fc8.png?v=1776471750&width=1200",
    price: "$141",
    memberPrice: "$127",
    currency: "CAD",
    salePercent: 10,
    rating: 4.8,
    reviews: 8771,
    optionLabel: "Skin type",
    variantType: "chips",
    variants: [
      { id: "n2d", label: "Normal to Dry" },
      { id: "combo", label: "Combination" },
      { id: "vdry", label: "Very Dry" },
      { id: "oily", label: "Oily" },
    ],
  },
  {
    id: "dmc",
    title: "Daily Moisturizing Cream",
    href: "https://www.riversol.com/products/daily-moisturizing-cream",
    description: "Everyday moisturizer for brighter, smoother skin.",
    primary:
      "https://www.riversol.com/cdn/shop/products/dmc_8215c1a3-320f-40e2-8cda-af33d45a4928.png?v=1776098827&width=1000",
    secondary:
      "https://www.riversol.com/cdn/shop/files/DMC_Texture.jpg?v=1776098818&width=1200",
    price: "$41",
    memberPrice: "$37",
    currency: "CAD",
    salePercent: 16,
    rating: 4.9,
    reviews: 2226,
    optionLabel: "Size",
    variantType: "chips",
    variants: [
      { id: "50", label: "50mL" },
      { id: "15", label: "15mL" },
    ],
  },
  {
    id: "spf",
    title: "Daily Glow Mineral Sunscreen SPF 50+",
    href: "https://www.riversol.com/products/daily-glow-mineral-sunscreen-spf-50",
    description: "Hydrating 100% mineral SPF with niacinamide.",
    primary:
      "https://www.riversol.com/cdn/shop/files/Daily-Glow-Mineral-Sunscreen-over-texture-and-aplicator_UNTINTED.png?v=1776126826&width=1000",
    secondary:
      "https://www.riversol.com/cdn/shop/files/Jayda-Lifestyle-SPF.jpg?v=1776126828&width=1200",
    price: "$39",
    memberPrice: "$36",
    currency: "CAD",
    salePercent: 20,
    rating: 4.2,
    reviews: 246,
    optionLabel: "Shade",
    variantType: "swatches",
    variants: [
      { id: "s100", label: "Shade 100", color: "#bf8d7a" },
      { id: "s200", label: "Shade 200", color: "#773d31" },
      { id: "untinted", label: "Untinted", color: "#f4efe9", soldOut: true },
    ],
  },
  {
    id: "phc",
    title: "Peptide Hand Cream",
    href: "https://www.riversol.com/products/peptide-hand-cream",
    description:
      "A light hand cream for dark spots with niacinamide + tetrapeptide-30.",
    primary:
      "https://www.riversol.com/cdn/shop/files/Peptide_Hand_Cream.png?v=1786982562&width=1200",
    secondary:
      "https://www.riversol.com/cdn/shop/files/product-description-100_27a86e30-58e2-4d4a-aee1-1b8e00539c4c.jpg?v=1786982525&width=1200",
    price: "$19",
    memberPrice: "$18",
    currency: "CAD",
    badge: "New",
    rating: 4.8,
    reviews: 152,
    variants: [],
  },
];

const Stars = ({ score, count }) => (
  <div className="inline-flex items-center gap-1.5 text-[12px] text-neutral-500">
    <span className="tracking-[0.2em] text-riversol-blue text-[11px]">★★★★★</span>
    <span className="tabular-nums text-neutral-600">{score.toFixed(1)}</span>
    <span className="text-neutral-400">({count.toLocaleString()})</span>
  </div>
);

const PriceBlock = ({ p, compact = false }) => (
  <div className="flex flex-col gap-1">
    <div className="inline-flex items-baseline gap-1.5 tabular-nums text-neutral-500">
      <span className={`${compact ? "text-[16px]" : "text-[17px]"} leading-none`}>{p.price}</span>
      {p.currency && (
        <span className="font-mono text-[9.5px] tracking-[0.16em] uppercase">{p.currency}</span>
      )}
    </div>
    <div className="inline-flex items-baseline gap-2 tabular-nums">
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`${compact ? "text-[22px]" : "text-[24px]"} leading-none font-semibold text-riversol-blue cursor-help border-b border-dotted border-riversol-blue/50 pb-0.5`}
            data-testid={`member-price-${p.id}`}
          >
            {p.memberPrice}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px] text-center leading-relaxed">
          Riversol+ members save 10% on every order — $15/mo store credit, free
          shipping. Cancel anytime.
        </TooltipContent>
      </Tooltip>
      <span className="inline-flex items-center rounded-full bg-riversol-blue text-white font-mono text-[9px] tracking-[0.16em] uppercase px-2 py-[3px] leading-none">
        R+ Member
      </span>
    </div>
  </div>
);

const VariantPill = ({ v, active, isSwatch, onSelect, size = "sm", testid }) => {
  if (isSwatch) {
    return (
      <button
        type="button"
        disabled={v.soldOut}
        onClick={() => !v.soldOut && onSelect(v.id)}
        title={v.label + (v.soldOut ? " · Sold out" : "")}
        aria-label={v.label}
        className={`relative shrink-0 rounded-full border transition-all duration-200 ${
          size === "lg" ? "w-8 h-8" : "w-6 h-6"
        } ${
          active
            ? "border-riversol-blue ring-2 ring-riversol-blue/25 ring-offset-2 ring-offset-white"
            : v.soldOut
            ? "border-neutral-200 opacity-40 cursor-not-allowed"
            : "border-neutral-300 hover:border-neutral-500"
        }`}
        style={{ background: v.color }}
        data-testid={testid}
      >
        {v.soldOut && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="block w-[120%] h-px bg-neutral-400 rotate-45" />
          </span>
        )}
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={v.soldOut}
      onClick={() => !v.soldOut && onSelect(v.id)}
      className={`shrink-0 whitespace-nowrap rounded-full border transition-colors ${
        size === "lg" ? "text-[13px] px-4 py-2" : "text-[11px] px-3 py-1.5"
      } ${
        active
          ? "bg-riversol-blue text-white border-riversol-blue"
          : v.soldOut
          ? "border-neutral-200 text-neutral-300 cursor-not-allowed"
          : "border-neutral-300 text-neutral-700 hover:border-riversol-blue hover:text-riversol-blue"
      }`}
      data-testid={testid}
    >
      {v.label}
    </button>
  );
};

// On-card single-select variant picker: shows up to 2 options + a "+N"
// popover (Quick-Start styled) with the remaining official options.
const CardVariantSelect = ({ p, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const isSwatch = p.variantType === "swatches";
  const visible = p.variants.slice(0, 2);
  const hidden = p.variants.slice(2);
  const selectedVariant = p.variants.find((v) => v.id === selected);
  const pick = (id) => {
    onSelect(id);
    setOpen(false);
  };
  return (
    <div data-testid={`cardvariant-${p.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">
          {p.optionLabel}
        </span>
        {selectedVariant && (
          <span className="text-[11px] text-riversol-blue truncate">· {selectedVariant.label}</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {visible.map((v) => (
          <VariantPill
            key={v.id}
            v={v}
            active={selected === v.id}
            isSwatch={isSwatch}
            onSelect={onSelect}
            testid={`${isSwatch ? "swatch" : "chip"}-${p.id}-${v.id}`}
          />
        ))}
        {hidden.length > 0 && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`shrink-0 rounded-full border text-[11px] px-3 py-1.5 transition-colors ${
                  selected && hidden.some((h) => h.id === selected)
                    ? "bg-riversol-blue text-white border-riversol-blue"
                    : "border-neutral-300 text-neutral-700 hover:border-riversol-blue hover:text-riversol-blue"
                }`}
                data-testid={`cardvariant-more-${p.id}`}
              >
                +{hidden.length}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-auto min-w-[200px] p-4 bg-white border border-neutral-200 rounded-xl shadow-[0_18px_50px_rgba(15,46,46,0.12)]"
              data-testid={`cardvariant-popover-${p.id}`}
            >
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">
                Choose {p.optionLabel.toLowerCase()}
              </span>
              <div className="mt-3 flex flex-wrap items-center gap-2 max-w-[240px]">
                {p.variants.map((v) => (
                  <VariantPill
                    key={v.id}
                    v={v}
                    active={selected === v.id}
                    isSwatch={isSwatch}
                    onSelect={pick}
                    testid={`popvariant-${p.id}-${v.id}`}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
};

const AddToCartButton = ({ label = "Add to Cart", disabled = false, onAdd, className = "", testid }) => {
  const [added, setAdded] = useState(false);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (added || disabled) return;
        onAdd?.();
        setAdded(true);
        setTimeout(() => setAdded(false), 1600);
      }}
      className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[12px] tracking-[0.16em] uppercase font-medium transition-colors ${
        disabled
          ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
          : added
          ? "bg-riversol-blue/90 text-white"
          : "bg-riversol-blue text-white hover:bg-riversol-blueLight"
      } ${className}`}
      data-testid={testid}
    >
      {added ? (
        <>
          <Check size={15} strokeWidth={2} />
          Added
        </>
      ) : (
        <>
          <ShoppingBag size={14} strokeWidth={1.8} />
          {label}
        </>
      )}
    </button>
  );
};

const Badge = ({ p }) => {
  const text = p.salePercent ? `−${p.salePercent}%` : p.badge;
  if (!text) return null;
  return (
    <span
      className={`absolute top-3 right-3 z-10 font-mono text-[10px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-full tabular-nums ${
        p.salePercent
          ? "bg-riversol-blue text-white"
          : "bg-white text-neutral-900 border border-neutral-200"
      }`}
    >
      {text}
    </span>
  );
};

const ProductCard = ({ p, index }) => {
  const hasVariants = p.variants?.length > 0;
  // Auto-select the first available variant so Add to Cart is always active.
  const firstAvailable = p.variants?.find((v) => !v.soldOut)?.id ?? p.variants?.[0]?.id ?? null;
  const [selected, setSelected] = useState(firstAvailable);
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.05 + index * 0.06 }}
      className="group relative flex flex-col h-full rounded-3xl overflow-hidden border border-neutral-200/70 bg-white transition-[box-shadow,border-color] duration-500 hover:shadow-[0_18px_50px_rgba(15,46,46,0.09)] hover:border-riversol-blue/40"
      data-testid={`bestseller-card-${p.id}`}
    >
      <a href={p.href} aria-label={p.title} className="relative block shrink-0 aspect-square overflow-hidden bg-white">
        <img
          src={p.primary}
          alt={p.title}
          loading="lazy"
          draggable={false}
          className="absolute inset-0 w-full h-full object-contain p-6 lg:p-7 transition-[opacity,transform] duration-[700ms] ease-out group-hover:opacity-0 group-hover:scale-[0.94]"
        />
        <img
          src={p.secondary}
          alt=""
          aria-hidden
          loading="lazy"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover opacity-0 scale-[1.06] transition-[opacity,transform] duration-[700ms] ease-out group-hover:opacity-100 group-hover:scale-100"
        />
        <Badge p={p} />
      </a>

      <div className="flex flex-col flex-1 p-5">
        {/* Tight product-info group: title → description → rating */}
        <a
          href={p.href}
          className="block text-[16px] font-medium leading-[1.25] tracking-[-0.005em] text-neutral-900 hover:text-riversol-blue transition-colors"
        >
          {p.title}
        </a>
        <p className="mt-1 text-[13px] leading-[1.4] text-neutral-600">{p.description}</p>
        <div className="mt-1.5">
          <Stars score={p.rating} count={p.reviews} />
        </div>

        <div className="flex-1 min-h-[18px]" aria-hidden />

        {/* Variant selector (reserved height keeps price/CTA aligned) */}
        <div className="min-h-[56px] flex items-end">
          {hasVariants ? (
            <CardVariantSelect p={p} selected={selected} onSelect={setSelected} />
          ) : (
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400">
              One size
            </span>
          )}
        </div>

        <div className="mt-4">
          <PriceBlock p={p} />
        </div>

        <div className="mt-4">
          <AddToCartButton
            className="w-full"
            label="Add to Cart"
            testid={`bestseller-atc-${p.id}`}
          />
        </div>
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Quick Shop drawer — KEPT for reuse. Not wired to Best Sellers cards.
// ---------------------------------------------------------------------------
export const QuickShop = ({ product, open, onOpenChange }) => {
  const [selected, setSelected] = useState(null);
  const p = product;
  const activeId = selected ?? p?.variants?.[0]?.id ?? null;
  const isSwatch = p?.variantType === "swatches";
  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setSelected(null); }}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-white p-0 flex flex-col" data-testid="quickshop-drawer">
        {p && (
          <>
            <div className="p-6 border-b border-neutral-200">
              <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">Quick shop</span>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="aspect-square rounded-2xl bg-riversol-sand overflow-hidden">
                <img src={p.primary} alt={p.title} className="w-full h-full object-contain p-8" draggable={false} />
              </div>
              <h3 className="mt-6 text-[24px] leading-[1.15] tracking-[-0.01em] text-neutral-900">{p.title}</h3>
              <p className="mt-2 text-[14px] text-neutral-600 leading-relaxed">{p.description}</p>
              {p.memberPrice && <div className="mt-5"><PriceBlock p={p} compact /></div>}
              {p.variants?.length > 0 && (
                <div className="mt-7">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">{p.optionLabel}</span>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {p.variants.map((v) => (
                      <VariantPill key={v.id} v={v} active={activeId === v.id} isSwatch={isSwatch} onSelect={setSelected} size="lg" testid={`quickshop-variant-${v.id}`} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-neutral-200 space-y-3">
              <AddToCartButton className="w-full py-3.5" testid="quickshop-atc" />
              <a href={p.href} className="block text-center text-[11px] tracking-[0.16em] uppercase font-medium text-neutral-500 underline underline-offset-[5px] decoration-neutral-300 hover:text-riversol-blue hover:decoration-riversol-blue transition-colors" data-testid="quickshop-details">
                View full details
              </a>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ---------------------------------------------------------------------------
// Build Your Routine — official 4-step structure + "up to 25%" + free mask.
// ---------------------------------------------------------------------------
const BUILDER_STEPS = [
  {
    key: "cleanser",
    step: "Step 1 · Preparation",
    slot: "Select cleanser",
    products: [
      { id: "hcc", title: "Hydrating Cream Cleanser", price: 23, desc: "Gentle, purifying cleanser to hydrate & restore.", href: "https://www.riversol.com/products/hydrating-cream-cleanser", image: "https://www.riversol.com/cdn/shop/products/White_BG_-_Cream_Cleanser.png?v=1776098620&width=800" },
      { id: "rgc", title: "Refreshing Gel Cleanser", price: 18, desc: "Gentle, balancing cleanser to control oil.", href: "https://www.riversol.com/products/refreshing-gel-cleanser", image: "https://www.riversol.com/cdn/shop/files/NewGelCleanserV2.png?v=1776098691&width=800" },
    ],
  },
  {
    key: "serum",
    step: "Step 2 · Treatment",
    slot: "Select serum",
    products: [
      { id: "aas", title: "Anti-Aging Serum", price: 61, desc: "Vitamin C & E serum to soften fine lines & texture.", href: "https://www.riversol.com/products/anti-aging-serum", image: "https://www.riversol.com/cdn/shop/products/aa-serum.png?v=1776095507&width=800" },
      { id: "ars", title: "Anti-Redness Serum", price: 53, desc: "Vitamin-rich serum to calm redness & strengthen skin.", href: "https://www.riversol.com/products/anti-redness-serum", image: "https://www.riversol.com/cdn/shop/products/ar-serum_8ac94d83-4bb7-4e8a-b05f-292af5868b7f.png?v=1776095594&width=800" },
      { id: "cor", title: "Corrector V2", price: 53, desc: "Brightening serum to fade dark spots & sun damage.", href: "https://www.riversol.com/products/the-corrector-v2", image: "https://www.riversol.com/cdn/shop/files/Corrector_V2.png?v=1776115112&width=800" },
      { id: "ret", title: "Retinol Treatment", price: 53, desc: "0.5% retinol visibly firms & smooths without irritation.", href: "https://www.riversol.com/products/retinol-treatment", image: "https://www.riversol.com/cdn/shop/files/NewRetinol-V2.png?v=1776101320&width=800" },
      { id: "peel", title: "Exfoliating Glycolic Peel", price: 36, desc: "4% glycolic peel smooths & brightens dull skin tone.", href: "https://www.riversol.com/products/exfoliating-glycolic-peel", image: "https://www.riversol.com/cdn/shop/products/WhiteBG-Peel-XL.png?v=1776095551&width=800" },
    ],
  },
  {
    key: "moisturizer",
    step: "Step 3 · Hydration",
    slot: "Select moisturizer",
    products: [
      { id: "dmc", title: "Daily Moisturizing Cream", price: 32, desc: "Everyday moisturizer for brighter, smoother skin.", href: "https://www.riversol.com/products/daily-moisturizing-cream", image: "https://www.riversol.com/cdn/shop/products/dmc_8215c1a3-320f-40e2-8cda-af33d45a4928.png?v=1776098827&width=800" },
      { id: "conc", title: "Moisturizing Concentrate", price: 38, desc: "Ultra-rich hyaluronic acid & vitamin E cream.", href: "https://www.riversol.com/products/moisturizing-concentrate", image: "https://www.riversol.com/cdn/shop/products/concentrate_304fc294-9cce-4bf5-990a-d8e5cbb07fd7.png?v=1776095471&width=800" },
      { id: "lotion", title: "Lightweight Moisturizing Lotion", price: 32, desc: "Light moisturizer to soften fine lines & refine texture.", href: "https://www.riversol.com/products/lightweight-moisturizing-lotion", image: "https://www.riversol.com/cdn/shop/files/lotion-thumb_3866e9ee-704a-466c-b343-af1a1877a536.png?v=1776098738&width=800" },
    ],
  },
  {
    key: "sunscreen",
    step: "Step 4 · Protection",
    slot: "Select sunscreen",
    products: [
      { id: "spf", title: "Daily Glow Mineral Sunscreen SPF 50+", price: 39, desc: "Hydrating 100% mineral SPF with niacinamide.", href: "https://www.riversol.com/products/daily-glow-mineral-sunscreen-spf-50", image: "https://www.riversol.com/cdn/shop/files/Daily-Glow-Mineral-Sunscreen-over-texture-and-aplicator_UNTINTED.png?v=1776126826&width=800", variantType: "swatches", optionLabel: "Shade", variants: [ { id: "s100", label: "Shade 100", color: "#bf8d7a" }, { id: "s200", label: "Shade 200", color: "#773d31" }, { id: "untinted", label: "Untinted", color: "#f4efe9", soldOut: true } ] },
      { id: "lipoil", title: "Broad Spectrum SPF 40+ Tinted Sunscreen Lip Oil", price: 24, desc: "Mineral SPF lip oil in buildable, glossy tints.", href: "https://www.riversol.com/products/broad-spectrum-spf-40-tinted-sunscreen-lip-oil", image: "https://www.riversol.com/cdn/shop/files/Broad-Spectrum-SPF-40-Lip-Oil_PPY.png?v=1776097706&width=800", variantType: "chips", optionLabel: "Tint", variants: [ { id: "poppy", label: "Poppy" }, { id: "popsicle", label: "Popsicle" }, { id: "rosewood", label: "Rosewood", soldOut: true } ] },
    ],
  },
];

// Reused right-side drawer: shows a step's official products; user picks a
// product (+ required variant), then it populates the routine slot.
const StepDrawer = ({ step, open, onOpenChange, onConfirm }) => {
  const [productId, setProductId] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const reset = () => { setProductId(null); setVariantId(null); };
  const product = step?.products.find((p) => p.id === productId) || null;
  const needsVariant = product?.variants?.length > 0;
  const canAdd = product && (!needsVariant || variantId);
  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-white p-0 flex flex-col" data-testid="routine-drawer">
        {step && (
          <>
            <div className="p-6 border-b border-neutral-200">
              <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">{step.step}</span>
              <h3 className="mt-1 text-[20px] tracking-[-0.01em] text-neutral-900">{step.slot}</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {step.products.map((pr) => {
                const active = productId === pr.id;
                const prSwatch = pr.variantType === "swatches";
                return (
                  <div
                    key={pr.id}
                    className={`rounded-2xl border transition-colors ${active ? "border-riversol-blue bg-riversol-sand/40" : "border-neutral-200 hover:border-neutral-300"}`}
                  >
                    <button
                      type="button"
                      onClick={() => { setProductId(pr.id); setVariantId(null); }}
                      className="w-full flex items-center gap-4 p-3 text-left"
                      data-testid={`routine-product-${step.key}-${pr.id}`}
                    >
                      <span className="w-16 h-16 shrink-0 rounded-xl bg-white border border-neutral-100 overflow-hidden">
                        <img src={pr.image} alt={pr.title} className="w-full h-full object-contain p-1.5" draggable={false} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-medium text-neutral-900 leading-tight">{pr.title}</span>
                        <span className="block mt-0.5 text-[12px] text-neutral-500 leading-snug line-clamp-2">{pr.desc}</span>
                      </span>
                      <span className="text-[15px] tabular-nums text-neutral-900 shrink-0">${pr.price}</span>
                    </button>
                    {active && pr.variants?.length > 0 && (
                      <div className="px-3 pb-3 -mt-1">
                        <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-neutral-500">{pr.optionLabel}</span>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {pr.variants.map((v) => (
                            <VariantPill key={v.id} v={v} active={variantId === v.id} isSwatch={prSwatch} onSelect={setVariantId} testid={`routine-variant-${step.key}-${pr.id}-${v.id}`} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="p-6 border-t border-neutral-200">
              <button
                type="button"
                disabled={!canAdd}
                onClick={() => {
                  const variant = product.variants?.find((v) => v.id === variantId) || null;
                  onConfirm(step.key, product, variant);
                  onOpenChange(false);
                  reset();
                }}
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[12px] tracking-[0.16em] uppercase font-medium transition-colors ${canAdd ? "bg-riversol-blue text-white hover:bg-riversol-blueLight" : "bg-neutral-200 text-neutral-400 cursor-not-allowed"}`}
                data-testid="routine-drawer-add"
              >
                {needsVariant && !variantId ? `Select ${product.optionLabel.toLowerCase()}` : "Add to routine"}
                {canAdd && <ArrowRight size={14} strokeWidth={1.8} />}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

const RoutineSlot = ({ step, selection, onOpen, onRemove }) => {
  if (!selection) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="group w-full flex flex-col items-start justify-between gap-4 rounded-2xl border border-dashed border-white/25 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/40 transition-colors p-4 min-h-[124px] text-left"
        data-testid={`routine-slot-${step.key}`}
      >
        <span className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-white/45">{step.step}</span>
        <span className="inline-flex items-center gap-2 text-[13px] tracking-[0.06em] text-white">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/12 group-hover:bg-white/20 transition-colors">
            <Plus size={13} strokeWidth={2} />
          </span>
          {step.slot}
        </span>
      </button>
    );
  }
  const { product, variant } = selection;
  return (
    <div className="relative w-full flex flex-col justify-between gap-3 rounded-2xl border border-black/5 bg-[#EDF3F3] p-4 min-h-[124px]" data-testid={`routine-slot-${step.key}`}>
      <span className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-neutral-400">{step.step}</span>
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 shrink-0 rounded-lg bg-riversol-sand overflow-hidden">
          <img src={product.image} alt={product.title} className="w-full h-full object-contain p-1" draggable={false} />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block text-[13px] font-medium text-neutral-900 leading-tight line-clamp-2">{product.title}</span>
          {variant && <span className="block text-[11px] text-neutral-500">{variant.label}</span>}
        </div>
        <span className="text-[14px] tabular-nums text-neutral-900 shrink-0">${product.price}</span>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" onClick={onOpen} className="text-[10px] tracking-[0.16em] uppercase font-medium text-riversol-blue hover:underline underline-offset-2" data-testid={`routine-change-${step.key}`}>Change</button>
        <button type="button" onClick={onRemove} className="text-[10px] tracking-[0.16em] uppercase font-medium text-neutral-400 hover:text-neutral-700" data-testid={`routine-remove-${step.key}`}>Remove</button>
      </div>
    </div>
  );
};

const BuildRoutine = ({ routine, setRoutine }) => {
  const [drawerStep, setDrawerStep] = useState(null);
  const [added, setAdded] = useState(false);

  const filled = Object.values(routine).filter(Boolean);
  const subtotal = filled.reduce((s, x) => s + x.product.price, 0);
  const complete = filled.length === 4;
  const savings = complete ? Math.round(subtotal * 0.25) : 0;
  const total = subtotal - savings;

  const confirm = (key, product, variant) => setRoutine((r) => ({ ...r, [key]: { product, variant } }));
  const remove = (key) => setRoutine((r) => ({ ...r, [key]: null }));

  return (
    <div className="mt-4 rounded-2xl bg-riversol-blue/95 border border-white/10 p-6 lg:p-8" data-testid="build-routine">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-6">
        <div>
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/55">Build your routine</span>
          <h3 className="mt-1 text-[24px] lg:text-[28px] leading-[1.1] tracking-[-0.01em] text-white">
            Save up to 25% + a free PDRN Hydrogel Mask 4-Pack.
          </h3>
        </div>
        <p className="text-[13px] text-white/60 max-w-[320px] lg:text-right">
          Mix &amp; match your 4-step routine. Complete all four steps to unlock the full 25% off.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {BUILDER_STEPS.map((step) => (
          <RoutineSlot
            key={step.key}
            step={step}
            selection={routine[step.key]}
            onOpen={() => setDrawerStep(step)}
            onRemove={() => remove(step.key)}
          />
        ))}
      </div>

      {/* Progress toward the max offer */}
      <div className="mt-6 flex items-center gap-4">
        <div className="relative h-1 flex-1 rounded-full bg-white/15 overflow-hidden">
          <motion.span
            className="absolute inset-y-0 left-0 bg-white rounded-full"
            animate={{ width: `${(filled.length / 4) * 100}%` }}
            transition={{ type: "spring", stiffness: 160, damping: 26 }}
          />
        </div>
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60 whitespace-nowrap tabular-nums">
          {filled.length}/4 steps
        </span>
      </div>

      {/* Summary */}
      <div className="mt-6 rounded-xl bg-[#EDF3F3] p-5 lg:p-6">
        <div className="flex items-center justify-between text-[14px] text-neutral-700">
          <span>Routine subtotal</span>
          <span className="tabular-nums">${subtotal}</span>
        </div>
        <div className="mt-2 flex items-center justify-between text-[14px]">
          <span className={complete ? "text-riversol-blue font-medium" : "text-neutral-400"}>
            Bundle savings {complete ? "(25%)" : "— unlocks at 4 steps"}
          </span>
          <span className={`tabular-nums ${complete ? "text-riversol-blue font-medium" : "text-neutral-400"}`}>−${savings}</span>
        </div>
        {complete && (
          <div className="mt-2 flex items-center justify-between text-[14px]">
            <span className="text-riversol-blue font-medium">PDRN Hydrogel Mask 4-Pack</span>
            <span className="tabular-nums text-riversol-blue font-medium">FREE</span>
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-black/10 flex items-center justify-between">
          <span className="text-[15px] font-medium text-neutral-900">Total</span>
          <span className="text-[24px] font-semibold tabular-nums text-neutral-900" data-testid="routine-total">${total}</span>
        </div>
        <button
          type="button"
          disabled={filled.length === 0}
          onClick={() => { if (filled.length === 0) return; setAdded(true); setTimeout(() => setAdded(false), 1800); }}
          className={`mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[12px] tracking-[0.16em] uppercase font-medium transition-colors ${
            filled.length === 0 ? "bg-neutral-200 text-neutral-400 cursor-not-allowed" : added ? "bg-riversol-blue/90 text-white" : "bg-riversol-blue text-white hover:bg-riversol-blueLight"
          }`}
          data-testid="routine-add-to-cart"
        >
          {added ? (<><Check size={15} strokeWidth={2} />Routine added</>) : (<><ShoppingBag size={14} strokeWidth={1.8} />Add routine to cart</>)}
        </button>
      </div>

      <StepDrawer
        step={drawerStep}
        open={!!drawerStep}
        onOpenChange={(o) => { if (!o) setDrawerStep(null); }}
        onConfirm={confirm}
      />
    </div>
  );
};

export const BestSellers = ({ titleTail = "customers repurchase.", sectionTestId = "home-best-sellers", showBundle = true }) => {
  const sectionRef = useRef(null);
  const revealed = useInView(sectionRef, { once: true, margin: "-100px" });
  const [expanded, setExpanded] = useState(false);
  // Routine selections live here so they persist when the bundle collapses.
  const [routine, setRoutine] = useState({ cleanser: null, serum: null, moisturizer: null, sunscreen: null });
  const selectedCount = Object.values(routine).filter(Boolean).length;

  return (
    <TooltipProvider delayDuration={120}>
      <section ref={sectionRef} className="bg-riversol-sand py-24 lg:py-32" data-testid={sectionTestId}>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between gap-8 mb-12 lg:mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={revealed ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="text-[36px] lg:text-[48px] leading-[1.05] tracking-[-0.02em] text-neutral-900 max-w-[720px] font-light"
            >
              Formulas that{" "}
              <span className="italic text-riversol-blue font-normal">{titleTail}</span>
            </motion.h2>
            <motion.a
              href="https://www.riversol.com/collections/all"
              initial={{ opacity: 0 }}
              animate={revealed ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:inline-flex items-center gap-2 rounded-full border border-neutral-300 px-6 py-3 text-[12px] tracking-[0.14em] uppercase font-medium text-neutral-900 hover:border-riversol-blue hover:text-riversol-blue transition-colors"
              data-testid="bestseller-shop-all"
            >
              Shop all bestsellers
              <ArrowRight size={14} strokeWidth={1.8} />
            </motion.a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {PRODUCTS.map((p, i) => (
              <ProductCard key={p.id} p={p} index={i} />
            ))}
          </div>

          <div className="mb-8 flex justify-center lg:hidden">
            <a
              href="https://www.riversol.com/collections/all"
              className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-6 py-3 text-[12px] tracking-[0.14em] uppercase font-medium text-neutral-900 hover:border-riversol-blue hover:text-riversol-blue transition-colors"
              data-testid="bestseller-shop-all-mobile"
            >
              Shop all bestsellers
              <ArrowRight size={14} strokeWidth={1.8} />
            </a>
          </div>

          {/* Bundle & Save — collapsed bar that expands the builder inline */}
          {showBundle && (
          <div className="mt-8 lg:mt-10">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="group w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-2xl bg-riversol-blue text-white px-7 lg:px-10 py-6 lg:py-7 text-left"
              data-testid="bundle-save-strip"
            >
              <div className="flex items-center gap-5">
                <span className="relative flex items-center justify-center w-11 h-11 rounded-full bg-white/10 shrink-0 transition-colors group-hover:bg-white/20" data-testid="bundle-toggle-icon">
                  {!expanded && (
                    <>
                      <span className="absolute inset-0 rounded-full bg-white/20 animate-ping" style={{ animationDuration: "2.2s" }} aria-hidden />
                      <span className="absolute inset-0 rounded-full ring-1 ring-white/30" aria-hidden />
                    </>
                  )}
                  <span className="relative flex transition-transform duration-300 group-hover:rotate-90">
                    {expanded ? <Minus size={18} strokeWidth={1.8} /> : <Plus size={18} strokeWidth={1.8} />}
                  </span>
                  {/* Selection count bubble — only when collapsed with picks */}
                  {!expanded && selectedCount > 0 && (
                    <motion.span
                      key={selectedCount}
                      initial={{ scale: 0.4, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 22 }}
                      className="absolute -top-1.5 -right-1.5 z-10 flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-white text-riversol-blue text-[11px] font-semibold tabular-nums ring-2 ring-riversol-blue shadow-sm"
                      data-testid="bundle-count-bubble"
                    >
                      {selectedCount}
                    </motion.span>
                  )}
                </span>
                <div>
                  <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/60">Bundle &amp; Save</span>
                  <p className="mt-1 text-[19px] lg:text-[22px] leading-[1.2] tracking-[-0.01em]">
                    Build your routine and save up to 25%.
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white text-riversol-blue px-6 py-3 text-[12px] tracking-[0.16em] uppercase font-medium whitespace-nowrap group-hover:gap-3 transition-[gap] duration-300">
                {expanded ? "Close" : selectedCount > 0 ? "Continue routine" : "Build routine"}
                {expanded ? <Minus size={14} strokeWidth={1.8} /> : <ArrowRight size={14} strokeWidth={1.8} />}
              </span>
            </button>

            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  key="builder"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <BuildRoutine routine={routine} setRoutine={setRoutine} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          )}
        </div>
      </section>
    </TooltipProvider>
  );
};

export default BestSellers;
